import { useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { Add, Delete, ExpandMore } from "@mui/icons-material";
import jsonrepair from "jsonrepair";
import localize from "../../../common/localize";
import useGlobalContext from "../../../hooks/useGlobalContext";
import {
  getInputLabelProps,
  getInputProps,
  PopperFullWidth,
} from "../../../common/appStyles";
import Combobox from "../../../components/common/Combobox";
import ErrorMessage from "../../../components/common/ErrorMessage";
import {
  getProfileNameByForm,
  getActionNameByKey,
} from "../../../common/utils";
import {
  getProfiles,
  getModuleActions,
  getDefaultActions,
} from "../../../common/EntitiesManagement";
import NotificationItem from "../../../components/common/NotificationItem";

export default function AddOrEditProfile() {
  const { profileModel, setProfileModel, profileModules, setProfileModules } =
    useGlobalContext();

  const [errorMessage, setErrorMessage] = useState("");
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [selectedModule, setSelectedModule] = useState(null);

  const handleModuleChange = (value) => {
    if (!value) {
      setSelectedModule(null);
    }
    setSelectedModule(value);
  };

  const handleChange = (e) => {
    setProfileModel({
      ...profileModel,
      [e.target.name]: e.target.value,
    });
  };

  const handleAddModule = () => {
    if (
      profileModules.filter(
        (profileModule) => profileModule.Form === selectedModule.Form
      ).length === 0
    ) {
      setProfileModules([
        ...profileModules,
        {
          Form: selectedModule.Form,
          Restriction: "ACCESS",
          Actions: { ACCESS: true },
        },
      ]);
      setSelectedModule(null);
    }
    setSelectedModule(null);
  };

  const handleDeleteProfileModule = (moduleObj) => {
    setProfileModules(
      profileModules.filter(
        (profileModule) => profileModule.Form !== moduleObj.Form
      )
    );
  };

  const handleActionToggle = (moduleForm, actionKey) => {
    setProfileModules(
      profileModules.map((mod) => {
        if (mod.Form !== moduleForm) return mod;
        const currentActions = mod.Actions || {};
        const newActions = {
          ...currentActions,
          [actionKey]: !currentActions[actionKey],
        };
        return {
          ...mod,
          Restriction: newActions.ACCESS ? "ACCESS" : undefined,
          Actions: newActions,
        };
      })
    );
  };

  const handleToggleAllActions = (moduleForm, actions, selectAll) => {
    setProfileModules(
      profileModules.map((mod) => {
        if (mod.Form !== moduleForm) return mod;
        const newActions = { ...(mod.Actions || {}) };
        actions.forEach((action) => {
          newActions[action] = selectAll;
        });
        return {
          ...mod,
          Restriction: newActions.ACCESS ? "ACCESS" : undefined,
          Actions: newActions,
        };
      })
    );
  };

  const getActiveActionsCount = (moduleObj) => {
    if (!moduleObj.Actions) return 0;
    return Object.values(moduleObj.Actions).filter(Boolean).length;
  };

  useEffect(() => {
    if (profileModel?.Id) {
      const parsed = JSON.parse(jsonrepair(profileModel.Description));
      const migrated = parsed.map((mod) => {
        if (mod.Restriction === "ACCESS" && (!mod.Actions || !mod.Actions.ACCESS)) {
          return {
            ...mod,
            Actions: { ...mod.Actions, ACCESS: true },
          };
        }
        return mod;
      });
      setProfileModules(migrated);
    }
  }, [profileModel]);

  const renderModuleActions = (moduleObj) => {
    const availableActions = getModuleActions(moduleObj.Form);
    if (availableActions.length === 0) return null;

    const defaultActions = getDefaultActions();
    const specialActions = availableActions.filter(
      (a) => !defaultActions.includes(a)
    );
    const currentActions = moduleObj.Actions || {};
    const allChecked = availableActions.every((a) => currentActions[a]);
    const noneChecked = availableActions.every((a) => !currentActions[a]);

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#5A6A7A", textTransform: "uppercase" }}>
            {localize.actions}
          </Typography>
          <Button
            size="small"
            sx={{ fontSize: "0.65rem", textTransform: "none" }}
            onClick={() =>
              handleToggleAllActions(
                moduleObj.Form,
                availableActions,
                !allChecked
              )
            }
          >
            {allChecked ? localize.no : localize.yes} — {allChecked ? "Desmarcar todo" : "Marcar todo"}
          </Button>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 0,
          }}
        >
          {defaultActions
            .filter((a) => availableActions.includes(a))
            .map((action) => (
              <FormControlLabel
                key={action}
                control={
                  <Checkbox
                    size="small"
                    checked={!!currentActions[action]}
                    onChange={() =>
                      handleActionToggle(moduleObj.Form, action)
                    }
                  />
                }
                label={
                  <Typography sx={{ fontSize: "0.8rem" }}>
                    {getActionNameByKey(action)}
                  </Typography>
                }
              />
            ))}
        </Box>

        {specialActions.length > 0 && (
          <>
            <Typography
              sx={{
                fontSize: "0.7rem",
                fontWeight: 600,
                color: "#5A6A7A",
                textTransform: "uppercase",
                mt: 0.5,
              }}
            >
              {localize.special_permissions}
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 0,
              }}
            >
              {specialActions.map((action) => (
                <FormControlLabel
                  key={action}
                  control={
                    <Checkbox
                      size="small"
                      checked={!!currentActions[action]}
                      onChange={() =>
                        handleActionToggle(moduleObj.Form, action)
                      }
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: "0.8rem" }}>
                      {getActionNameByKey(action)}
                    </Typography>
                  }
                />
              ))}
            </Box>
          </>
        )}
      </Box>
    );
  };

  return (
    <div style={{ width: "100%" }}>
      {showErrorModal && (
        <ErrorMessage
          message={errorMessage}
          title={localize.error_unexpected}
          showModal={showErrorModal}
          setShowModal={setShowErrorModal}
        />
      )}

      <form className="w-full mt-2 flex flex-col gap-2">
        <div className="mb-4">
          <TextField
            required
            InputLabelProps={getInputLabelProps()}
            inputProps={getInputProps()}
            size="small"
            fullWidth
            name="Name"
            id="Name"
            value={profileModel.Name}
            label={localize.name}
            variant="outlined"
            onChange={handleChange}
          />
        </div>

        <div className="flex items-center w-full gap-2">
          <Combobox
            id="Module"
            fieldLabel={localize.module}
            fieldOptions={getProfiles()}
            fieldValue={selectedModule}
            customOptionLabel={(option) => getProfileNameByForm(option.Form)}
            handleValueChange={handleModuleChange}
            popperFullWidth={PopperFullWidth}
          />
          <div>
            <Button
              disabled={!selectedModule}
              size="small"
              variant="contained"
              color="success"
              className="app_general_button_data app_success_button btn-sm"
              onClick={handleAddModule}
            >
              <span className="mr-2">{localize.add}</span>
              <Add className="text-lg" />
            </Button>
          </div>
        </div>

        <Divider />

        <div className="my-2 flex flex-col gap-1">
          {profileModules?.length > 0 ? (
            profileModules.map((moduleObj) => {
              const activeCount = getActiveActionsCount(moduleObj);

              return (
                <Accordion
                  key={moduleObj.Form}
                  variant="outlined"
                  disableGutters
                  sx={{
                    "&:before": { display: "none" },
                    border: "1px solid #E2E8F0",
                    borderRadius: "6px !important",
                    overflow: "hidden",
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMore />}
                    sx={{
                      backgroundColor: "#F8FAFC",
                      minHeight: 44,
                      "& .MuiAccordionSummary-content": {
                        alignItems: "center",
                        gap: 1,
                        my: 0.5,
                      },
                    }}
                  >
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteProfileModule(moduleObj);
                      }}
                    >
                      <Delete sx={{ fontSize: "1rem" }} />
                    </IconButton>
                    <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, flex: 1 }}>
                      {getProfileNameByForm(moduleObj.Form)}
                    </Typography>
                    {activeCount > 0 && (
                      <Chip
                        size="small"
                        label={activeCount === 1 ? `1 ${localize.restriction}` : `${activeCount} ${localize.restrictions.toLowerCase()}`}
                        color="warning"
                        variant="outlined"
                        sx={{ fontSize: "0.65rem", height: 22 }}
                      />
                    )}
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 2, py: 1.5 }}>
                    {renderModuleActions(moduleObj)}
                  </AccordionDetails>
                </Accordion>
              );
            })
          ) : (
            <NotificationItem
              notification={{ message: localize.no_restrictions }}
            />
          )}
        </div>
      </form>
    </div>
  );
}
